import { ACCOUNT_DETAILS, CHANGE_PASSWORD } from "@/lib/constants/api";
import API from "@/lib/constants/axios";
import { create } from "zustand";
import useUserStore from "./user.store";
import Cookies from "js-cookie";

type Earning = {
  showTotalEarnings: boolean;
  showWithdrawableEarnings: boolean;
};

interface SettingState {
  theme: boolean; // true -> dark theme, false -> light theme
  setTheme: (theme: boolean) => void;
  earnings: Earning;
  accountDetails: AccountDetailsPayload | undefined;
  toggleEarnings: (type: keyof Earning) => void;
  changePassword: (
    payload: ChangePasswordPayload,
  ) => Promise<undefined | { success: boolean; message: string }>;
  setupAccount: (
    payload: AccountDetailsPayload,
  ) => Promise<undefined | { success: boolean; message: string }>;
  fetchExistingAccountDetails: () => Promise<void>;
}

export interface ChangePasswordPayload {
  newPassword: string;
  userId: string;
}
export interface AccountDetailsPayload {
  account_number: string;
  bank_name: string;
  ifsc_code: string;
  branch: string;
  pan: string;
  account_holder_name: string;
}

const useSettingStore = create<SettingState>()((set, get) => ({
  theme: false,
  earnings: {
    showTotalEarnings: true,
    showWithdrawableEarnings: true,
  },
  accountDetails: undefined,
  setTheme: (theme) => {
    if (typeof window === "undefined") return; // Ensure it's running on the client

    let storedTheme = localStorage.getItem("theme");
    let newTheme =
      theme !== undefined
        ? theme
        : storedTheme === "true"
          ? true
          : !get().theme;

    set({ theme: newTheme });
    localStorage.setItem("theme", newTheme + "");

    document.documentElement.classList.toggle("dark", newTheme);
  },
  toggleEarnings: (type: keyof Earning) => {
    set({ earnings: { ...get().earnings, [type]: !get().earnings[type] } });
  },
  changePassword: async (payload: ChangePasswordPayload) => {
    try {
      payload.userId = useUserStore.getState().userId! ?? Cookies.get("sub");
      const res = await API.post(CHANGE_PASSWORD, payload);
      if (res.status == 201) {
        return {
          success: true,
          message: "Password changed successfully",
        };
      }
      return {
        success: false,
        message:
          "Something went wrong while changing password! please try again in sometime",
      };
    } catch (error) {
      return {
        success: false,
        message:
          "Something went wrong while changing password! please try again in sometime",
      };
    }
  },
  setupAccount: async (payload: AccountDetailsPayload) => {
    try {
      const res = await API.post(ACCOUNT_DETAILS, payload);
      if (res.status == 201) {
        return {
          success: true,
          message: "Account setup successfully!",
        };
      }
      return {
        success: false,
        message:
          "Something went wrong while setting up your account details! please try again in sometime",
      };
    } catch (error) {
      return {
        success: false,
        message:
          "Something went wrong while setting up your account details! please try again in sometime",
      };
    }
  },

  fetchExistingAccountDetails: async () => {
    try {
      const res = await API.get(ACCOUNT_DETAILS);
      if (res.status == 200) {
        set({ accountDetails: res.data });
      }
    } catch (error) {}
  },
}));

export default useSettingStore;
